import React from 'react';
import TaskRow from './TaskRow';
import { ProgressBar, accentStyle } from './TaskBits';
import { areaMeta, sortTasks, PEOPLE_BY_UID } from './taskModel';
import { computeProjectProgress } from '../../../services/ProjectService';

// Portal Phase C — project detail (C4). A project's task rollup + computed
// progress (completed ÷ total, never stored). Shows the open and completed work
// as the same task rows used everywhere else, plus an "Add task to project"
// affordance that opens the create form pre-bound to this project.
//
// 2026-07-26: this was an overlay sheet and is now an in-flow panel in the main
// column. Two reasons, and the second is the one that mattered. (1) Anthony
// reported it had no sorting; the module's sort control lives in the section
// header, which an overlay floats above and cannot reach — so in the modal there
// was nowhere to PUT a sort control that did not duplicate the one already
// there. (2) Two open cards already say that modal is too narrow to read
// without horizontal scrolling; adding a control to it would have deepened a
// surface we know is the wrong container. In the main column it inherits the
// header, the sort control, and the full content width for free.
//
// `sortBy` is the module's own sort state, so the ordering here matches every
// other list view rather than being a second opinion — it used to hardcode
// sortNeedsAction regardless of what the header said.
function ProjectDetail({
	project,
	allTasks,
	childrenByParent,
	projectsById,
	now,
	sortBy,
	onClose,
	rowHandlers,
	onAddTask,
	// Global show-completed preference. The project's Completed section is the
	// one place completed work was ALWAYS rendered, regardless of any setting.
	showCompleted = false,
}) {
	const prog = computeProjectProgress(allTasks, project.id);
	// Top-level tasks belonging to this project, split open / completed.
	const mineRoots = allTasks.filter(
		(t) => t.projectId === project.id && !(t.parentId && allTasks.some((p) => p.id === t.parentId))
	);
	const open = sortTasks(
		mineRoots.filter((t) => t.status !== 'completed'),
		sortBy
	);
	const completed = sortTasks(
		mineRoots.filter((t) => t.status === 'completed'),
		sortBy
	);
	const owner = project.ownerName ||
		(project.ownerUid && PEOPLE_BY_UID[project.ownerUid]?.name) ||
		null;

	const rowFor = (t) => (
		<TaskRow
			key={t.id}
			task={t}
			children={childrenByParent.get(t.id) || []}
			projectsById={projectsById}
			now={now}
			onOpen={rowHandlers.onOpen}
			onAdvance={rowHandlers.onAdvance}
			onEdit={rowHandlers.onEdit}
			onReassign={rowHandlers.onReassign}
			onReprioritize={rowHandlers.onReprioritize}
		/>
	);

	return (
		<div className="wt-project" style={accentStyle(project.area)}>
			<div className="wt-project__panel">
				{/* Back, not close. The panel replaces the list in the same column,
				    so the honest affordance is "return to where I came from" — an
				    X reads as dismissing an overlay that no longer exists. */}
				<button type="button" className="wt-project__back" onClick={onClose}>
					<i className="fa-sharp fa-solid fa-arrow-left" />
					All projects
				</button>

				<div className="wt-project__body">
					{/* No title here. As an overlay this panel had to name itself — it
					    floated over a header describing something else. In the main
					    column the section header names the project, directly beside
					    the sort control, so repeating it printed the same words twice
					    down the page. */}
					<div>
						<div className="wt-row__meta" style={{ flexWrap: 'wrap' }}>
							{project.area && (
								<span className="wt-tag">
									<span className="wt-tag__dot" />
									{areaMeta(project.area).label}
								</span>
							)}
							<span className={`wt-pill ${project.status === 'done' ? 'wt-pill--muted' : 'wt-pill--status'}`}>
								{project.status === 'someday' ? 'Someday' : project.status === 'done' ? 'Done' : 'Active'}
							</span>
							{owner && (
								<span className="wt-ref">
									<i className="fa-sharp fa-solid fa-user" />
									{owner}
								</span>
							)}
						</div>
					</div>

					{/* Progress card */}
					<div className="wt-props" style={{ padding: '0.9rem 1rem' }}>
						<div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
							<span style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-h4)', fontWeight: 600, color: 'var(--text-heading)' }}>
								{prog.done} of {prog.total} done
							</span>
							<span style={{ marginLeft: 'auto', fontWeight: 700, color: `var(--area-${project.area || 'website'})` }}>
								{prog.pct}%
							</span>
						</div>
						<div style={{ margin: '0.6rem 0 0.8rem' }}>
							<ProgressBar pct={prog.pct} accentArea={project.area} />
						</div>
						<div className="wt-statpills">
							<div className="wt-statpill">
								<div className="wt-statpill__num">{prog.todo}</div>
								<div className="wt-statpill__label">To-do</div>
							</div>
							<div className="wt-statpill">
								<div className="wt-statpill__num">{prog.inProgress}</div>
								<div className="wt-statpill__label">In progress</div>
							</div>
							<div className="wt-statpill">
								<div className="wt-statpill__num">{prog.done}</div>
								<div className="wt-statpill__label">Done</div>
							</div>
						</div>
					</div>

					{/* Open work */}
					<div>
						<div className="wt-section__title">Open · {open.length}</div>
						{open.length ? (
							<div className="wt-list">{open.map(rowFor)}</div>
						) : (
							<div className="wt-empty">
								<span className="wt-empty__bubble">
									<i className="fa-sharp-duotone fa-solid fa-circle-check" />
								</span>
								<div className="wt-empty__title">No open tasks</div>
								<div className="wt-empty__sub">Everything in this project is done.</div>
							</div>
						)}
					</div>

					{completed.length > 0 && (
						showCompleted ? (
							<div>
								<div className="wt-section__title">Completed · {completed.length}</div>
								<div className="wt-list">{completed.map(rowFor)}</div>
							</div>
						) : (
							/* The COUNT stays even when the rows do not: a project
							   whose finished work vanished entirely would read as
							   having had less done than it did. */
							<div className="wt-section__title wt-section__title--quiet">
								Completed · {completed.length} (hidden)
							</div>
						)
					)}

					<button
						type="button"
						className="btn-cyv-ghost"
						style={{ width: '100%' }}
						onClick={() => onAddTask?.(project)}>
						<i className="fa-sharp fa-solid fa-plus" />
						Add task to project
					</button>
				</div>
			</div>
		</div>
	);
}

export default ProjectDetail;
